import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import type { ApiError, ApiResponse, AuthResponse } from '@/types';
import { storage, StorageKeys } from '@/utils/storage';

// Read from Vite env or fall back to the dev proxy
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  // 20s — long enough for slow mobile networks, short enough to avoid hanging forever
  timeout: 20_000,
  // Spring's @RequestParam List<T> binds bare repeated keys (statuses=A&statuses=B).
  // Axios defaults to bracketed keys (statuses[]=A) which Spring silently ignores —
  // `indexes: null` makes it emit the bare repeated form instead.
  paramsSerializer: { indexes: null },
});

// -------------------------------------------------------------------
// Request interceptor — attach JWT
// -------------------------------------------------------------------
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = storage.get(StorageKeys.ACCESS_TOKEN);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// -------------------------------------------------------------------
// Response interceptor — auto-refresh on 401
// -------------------------------------------------------------------
interface RetryConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = storage.get(StorageKeys.REFRESH_TOKEN);
  if (!refreshToken) return null;

  try {
    // Use a fresh axios (no interceptors) to avoid recursion
    const resp = await axios.post<ApiResponse<AuthResponse>>(
      `${API_BASE_URL}/auth/refresh`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );
    const auth = resp.data?.data;
    if (!auth) return null;
    storage.set(StorageKeys.ACCESS_TOKEN, auth.accessToken);
    storage.set(StorageKeys.REFRESH_TOKEN, auth.refreshToken);
    return auth.accessToken;
  } catch {
    return null;
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const original = error.config as RetryConfig | undefined;

    // If 401 and we haven't retried yet, try refresh
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true;

      // De-dupe concurrent refreshes
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;

      if (newToken) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      }

      // Refresh failed — notify UI and redirect to /login
      storage.clear();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.dispatchEvent(new CustomEvent('session-expired'));
      }
    }

    return Promise.reject(normalizeError(error));
  }
);

// -------------------------------------------------------------------
// Normalize AxiosError → our ApiError shape for callers
// -------------------------------------------------------------------
export function normalizeError(err: unknown): ApiError {
  if (axios.isAxiosError(err)) {
    const resp = err.response?.data as ApiResponse<unknown> | undefined;
    return {
      message: resp?.message || err.message || 'Network error',
      errorCode: resp?.errorCode,
      fieldErrors: (resp?.data as Record<string, string> | undefined) || undefined,
      status: err.response?.status,
    };
  }
  return { message: err instanceof Error ? err.message : 'Unknown error' };
}

// -------------------------------------------------------------------
// Thin helpers that unwrap ApiResponse<T> → T
// -------------------------------------------------------------------
export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const resp = await apiClient.get<ApiResponse<T>>(url, config);
  return resp.data.data;
}

export async function apiPost<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig
): Promise<T> {
  const resp = await apiClient.post<ApiResponse<T>>(url, body, config);
  return resp.data.data;
}

export async function apiPut<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig
): Promise<T> {
  const resp = await apiClient.put<ApiResponse<T>>(url, body, config);
  return resp.data.data;
}

export async function apiPatch<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig
): Promise<T> {
  const resp = await apiClient.patch<ApiResponse<T>>(url, body, config);
  return resp.data.data;
}

export async function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const resp = await apiClient.delete<ApiResponse<T>>(url, config);
  return resp.data.data;
}
